// Analytics widgets — KPI tiles, trend chart, breakdowns, scatter, leaderboard,
// table and the filter bar. Themed to the institutional forest-green palette.
import { useState, type ReactNode } from 'react';
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
    PieChart, Pie, Cell, BarChart, Bar, ScatterChart, Scatter, ZAxis,
} from 'recharts';
import {
    TrendingUp, TrendingDown, ArrowUpDown, ChevronLeft, ChevronRight, Download, Inbox,
} from 'lucide-react';
import type {
    Kpi, TimeseriesPoint, BreakdownGroup, LeaderboardItem, PolygonRow,
} from './types';

// ── palette ────────────────────────────────────────────────────────────────
const FOREST = '#15803d';        // primary-700
const RISK: Record<string, string> = {
    low: '#16a34a', medium: '#d97706', high: '#dc2626', critical: '#991b1b',
    compliant: '#16a34a', pending: '#d97706', non_compliant: '#dc2626',
};
const DONUT = ['#15803d', '#65a30d', '#d97706', '#dc2626', '#0891b2', '#7c3aed', '#64748b'];
const riskColor = (v?: string | null) => RISK[(v || '').toLowerCase()] || '#94a3b8';

// ── formatting ───────────────────────────────────────────────────────────────
export function fmt(value: number, unit: string): string {
    if (unit === '%') return `${value.toFixed(1)}%`;
    if (unit === 'Δ') return value.toFixed(3);
    if (unit === 'ha') return value.toLocaleString(undefined, { maximumFractionDigits: 1 }) + ' ha';
    return value.toLocaleString();
}

// ── atoms ────────────────────────────────────────────────────────────────────
export function Card({ title, action, children, className = '' }: {
    title?: string; action?: ReactNode; children: ReactNode; className?: string;
}) {
    return (
        <div className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5 ${className}`}>
            {(title || action) && (
                <div className="flex items-center justify-between mb-4">
                    {title && <h3 className="font-display font-semibold text-gray-900 dark:text-white">{title}</h3>}
                    {action}
                </div>
            )}
            {children}
        </div>
    );
}

export function EmptyState({ message }: { message: string }) {
    return (
        <div className="flex flex-col items-center justify-center py-10 text-gray-400 dark:text-gray-500">
            <Inbox className="w-8 h-8 mb-2" />
            <p className="text-sm">{message}</p>
        </div>
    );
}

function Badge({ value }: { value?: string | null }) {
    if (!value) return <span className="text-gray-400">—</span>;
    const c = riskColor(value);
    return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
            style={{ background: `${c}1a`, color: c }}>
            {value.replace('_', ' ')}
        </span>
    );
}

// ── KPI tile ─────────────────────────────────────────────────────────────────
export function KpiTile({ kpi }: { kpi: Kpi }) {
    const hasDelta = kpi.delta_pct !== null && kpi.delta_pct !== undefined;
    const rising = (kpi.delta_pct || 0) >= 0;
    // "good" = movement in the good_when direction
    const good = hasDelta && ((kpi.good_when === 'up' && rising) || (kpi.good_when === 'down' && !rising));
    const deltaColor = !hasDelta ? 'text-gray-400' : good ? 'text-green-600' : 'text-red-600';
    const Arrow = rising ? TrendingUp : TrendingDown;
    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{kpi.label}</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">{fmt(kpi.value, kpi.unit)}</p>
            {hasDelta && (
                <div className={`mt-1 flex items-center gap-1 text-xs font-medium ${deltaColor}`}>
                    <Arrow className="w-3.5 h-3.5" />
                    {Math.abs(kpi.delta_pct as number).toFixed(1)}% vs prev.
                </div>
            )}
        </div>
    );
}

// ── trend chart ──────────────────────────────────────────────────────────────
export function TrendChart({ points }: { points: TimeseriesPoint[] }) {
    if (!points.length) return <EmptyState message="No data for this period" />;
    return (
        <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={points} margin={{ top: 5, right: 8, left: -10, bottom: 0 }}>
                <defs>
                    <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={FOREST} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={FOREST} stopOpacity={0} />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} width={48} />
                <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }} />
                <Area type="monotone" dataKey="value" stroke={FOREST} strokeWidth={2} fill="url(#trendFill)" />
            </AreaChart>
        </ResponsiveContainer>
    );
}

// ── breakdown: donut ─────────────────────────────────────────────────────────
export function BreakdownDonut({ groups, valueKey = 'count' }: {
    groups: BreakdownGroup[]; valueKey?: keyof BreakdownGroup;
}) {
    if (!groups.length) return <EmptyState message="No data" />;
    const data = groups.map((g) => ({ name: g.key, value: Number(g[valueKey]) || 0 }));
    return (
        <ResponsiveContainer width="100%" height={240}>
            <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {data.map((_, i) => <Cell key={i} fill={DONUT[i % DONUT.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }} />
            </PieChart>
        </ResponsiveContainer>
    );
}

// ── breakdown: horizontal bars ───────────────────────────────────────────────
export function BreakdownBars({ groups, valueKey = 'count', unit = 'count' }: {
    groups: BreakdownGroup[]; valueKey?: keyof BreakdownGroup; unit?: string;
}) {
    if (!groups.length) return <EmptyState message="No data" />;
    const data = groups.slice(0, 8).map((g) => ({ name: g.key.replace('_', ' '), value: Number(g[valueKey]) || 0 }));
    return (
        <ResponsiveContainer width="100%" height={Math.max(180, data.length * 34)}>
            <BarChart data={data} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} />
                <Tooltip formatter={(v: number) => fmt(v, unit)} contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }} />
                <Bar dataKey="value" fill={FOREST} radius={[0, 6, 6, 0]} barSize={16} />
            </BarChart>
        </ResponsiveContainer>
    );
}

// ── risk scatter (area vs deforestation %) ───────────────────────────────────
export function RiskScatter({ items }: { items: PolygonRow[] }) {
    const data = items
        .filter((x) => x.deforestation_percentage !== null)
        .map((x) => ({
            x: x.area_ha,
            y: x.deforestation_percentage as number,
            z: x.area_deforested_ha || 1,
            name: x.polygon_name,
            color: riskColor(x.risk_level || x.compliance_status),
        }));
    if (!data.length) return <EmptyState message="No analyzed lots in this period" />;
    return (
        <ResponsiveContainer width="100%" height={280}>
            <ScatterChart margin={{ top: 8, right: 12, left: -6, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" dataKey="x" name="Area" unit=" ha" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
                <YAxis type="number" dataKey="y" name="Deforestation" unit="%" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} width={40} />
                <ZAxis type="number" dataKey="z" range={[40, 400]} />
                <Tooltip cursor={{ strokeDasharray: '3 3' }}
                    formatter={(v: number, n: string) => n === 'Deforestation' ? `${v}%` : `${v} ha`}
                    contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }} />
                <Scatter data={data}>
                    {data.map((d, i) => <Cell key={i} fill={d.color} fillOpacity={0.7} />)}
                </Scatter>
            </ScatterChart>
        </ResponsiveContainer>
    );
}

// ── leaderboard ──────────────────────────────────────────────────────────────
export function Leaderboard({ items }: { items: LeaderboardItem[] }) {
    if (!items.length) return <EmptyState message="No lots to rank" />;
    const max = Math.max(...items.map((i) => i.deforestation_percentage || 0), 1);
    return (
        <div className="space-y-2.5">
            {items.map((it, i) => (
                <div key={it.polygon_id} className="flex items-center gap-3">
                    <span className="w-5 text-xs font-semibold text-gray-400 text-right">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{it.polygon_name}</span>
                            <span className="text-sm font-semibold" style={{ color: riskColor(it.risk_level) }}>
                                {(it.deforestation_percentage ?? 0).toFixed(1)}%
                            </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                            <div className="h-full rounded-full" style={{
                                width: `${((it.deforestation_percentage || 0) / max) * 100}%`,
                                background: riskColor(it.risk_level),
                            }} />
                        </div>
                        <p className="mt-0.5 text-xs text-gray-500 truncate">{it.producer_name || '—'} · {it.commodity} · {it.country || '—'}</p>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ── configurable-ish table (sortable + paginated) ────────────────────────────
type Col = { key: keyof PolygonRow; label: string; render?: (r: PolygonRow) => ReactNode; align?: 'left' | 'right' };

export function PolygonTable({ rows, onExport }: { rows: PolygonRow[]; onExport?: () => void }) {
    const [sortKey, setSortKey] = useState<keyof PolygonRow>('deforestation_percentage');
    const [asc, setAsc] = useState(false);
    const [page, setPage] = useState(0);
    const pageSize = 10;

    const cols: Col[] = [
        { key: 'polygon_name', label: 'Lot' },
        { key: 'producer_name', label: 'Producer', render: (r) => r.producer_name || '—' },
        { key: 'commodity', label: 'Commodity' },
        { key: 'country', label: 'Country', render: (r) => r.country || '—' },
        { key: 'area_ha', label: 'Area (ha)', align: 'right', render: (r) => r.area_ha.toLocaleString() },
        { key: 'deforestation_percentage', label: 'Defor. %', align: 'right', render: (r) => `${(r.deforestation_percentage ?? 0).toFixed(1)}%` },
        { key: 'compliance_status', label: 'Compliance', render: (r) => <Badge value={r.compliance_status} /> },
        { key: 'risk_level', label: 'Risk', render: (r) => <Badge value={r.risk_level} /> },
    ];

    const sorted = [...rows].sort((a, b) => {
        const av = a[sortKey], bv = b[sortKey];
        if (av === bv) return 0;
        if (av === null || av === undefined) return 1;
        if (bv === null || bv === undefined) return -1;
        const cmp = av < bv ? -1 : 1;
        return asc ? cmp : -cmp;
    });
    const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
    const slice = sorted.slice(page * pageSize, page * pageSize + pageSize);

    const setSort = (k: keyof PolygonRow) => {
        if (k === sortKey) setAsc(!asc); else { setSortKey(k); setAsc(false); }
        setPage(0);
    };

    if (!rows.length) return <EmptyState message="No analyzed lots — run an analysis to populate analytics" />;

    return (
        <div>
            <div className="overflow-x-auto -mx-1">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs text-gray-500 border-b border-gray-200 dark:border-gray-700">
                            {cols.map((c) => (
                                <th key={String(c.key)} className={`py-2 px-2 font-medium whitespace-nowrap ${c.align === 'right' ? 'text-right' : ''}`}>
                                    <button onClick={() => setSort(c.key)} className="inline-flex items-center gap-1 hover:text-gray-800 dark:hover:text-gray-200">
                                        {c.label}<ArrowUpDown className="w-3 h-3 opacity-40" />
                                    </button>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {slice.map((r) => (
                            <tr key={r.polygon_id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/40">
                                {cols.map((c) => (
                                    <td key={String(c.key)} className={`py-2.5 px-2 whitespace-nowrap ${c.align === 'right' ? 'text-right tabular-nums' : ''} text-gray-700 dark:text-gray-300`}>
                                        {c.render ? c.render(r) : String(r[c.key] ?? '—')}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                <span>{sorted.length} lots</span>
                <div className="flex items-center gap-3">
                    {onExport && (
                        <button onClick={onExport} className="inline-flex items-center gap-1 hover:text-primary-700">
                            <Download className="w-3.5 h-3.5" /> Export CSV
                        </button>
                    )}
                    <div className="flex items-center gap-1">
                        <button disabled={page === 0} onClick={() => setPage(page - 1)} className="p-1 rounded disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-700"><ChevronLeft className="w-4 h-4" /></button>
                        <span>{page + 1} / {pages}</span>
                        <button disabled={page >= pages - 1} onClick={() => setPage(page + 1)} className="p-1 rounded disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-700"><ChevronRight className="w-4 h-4" /></button>
                    </div>
                </div>
            </div>
        </div>
    );
}
